import React, { Suspense, useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl-next';

import Image from '@atlaskit/image';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { CardAction } from '@atlaskit/smart-card';
import { AnyAri } from '@atlassian/ari/any-ari';
import {
	type FocusAreaHealthKey,
	FocusAreaIcon,
	type FocusAreaStatusKey,
} from '@atlassian/focus-area-icon';
import {
	isDocumentEntity,
	isSearchAssetsPartial,
	isSearchAtlasPartial,
	isSearchConfluencePageBlogAttachment,
	isSearchConfluencePartial,
	isSearchJiraPartial,
	isSearchMercuryPartial,
	isSearchResultAssetsObject,
	isSearchResultAssetsObjectSchema,
	isSearchResultAssetsObjectType,
	isSearchResultGraphEntity,
	isSearchResultJiraBoard,
	isSearchResultJiraIssue,
	isSearchResultMercuryFocusAreaPartial,
	isSearchResultMercuryFocusAreaStatusUpdatePartial,
	isSearchResultSpfAskPartial,
	isSearchTalentPartial,
	type SearchAssetsPartial,
	type SearchAtlasPartial,
	type SearchConfluencePartial,
	SearchConfluenceSubtype,
	type SearchJiraPartial,
	type SearchMercuryPartial,
	type SearchPageNode,
	type SearchResultEntity,
	type SearchResultGraphDocument,
	type SearchResultType,
	type SearchTalentPartial,
} from '@atlassian/search-client';
import {
	getConfluenceUrlWithAnalyticsQueryParams,
	getGenericUrlWithAnalyticsQueryParams,
	highlightMatchedText,
} from '@atlassian/search-common';

import { ThirdPartyConfigs as ConstantThirdPartyConfigs } from '../../../../common/constants/3p-product-configs';
import { ReturnIcon } from '../../../../common/constants/icons/return';
import {
	is3pProductKeyForAnalytics,
	isProductKey,
	ProductKeys,
	type ProductKeys3P,
} from '../../../../common/constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import { SearchExperience } from '../../../../common/constants/search';
import { getSubtypeLabel } from '../../../../common/ui/3p-result-labeling';
import {
	type ActivityEventType,
	EventTypeMessage,
	VIEWED_EVENT,
	WORKED_EVENT,
} from '../../../../common/ui/event-type-message';
import { getStatus } from '../../../../common/ui/jira-status';
import {
	getAssetsEntityIcon,
	getAtlasEntityIcon,
	getConfluenceEntityIcon,
	getJiraEntityIcon,
} from '../../../../common/ui/product-entity-icon';
import { ProductIcon } from '../../../../common/ui/product-icon';
import { useItemsAnalyticsContext } from '../../../../common/ui/quick-find/items-analytics-provider';
import {
	SearchResult,
	type SearchResultEventArguments,
} from '../../../../common/ui/quick-find/search-dialog-result';
import { type SearchMetadataPart } from '../../../../common/ui/search-metadata';
import { SearchResultType as SearchResultTypeText } from '../../../../common/ui/search-result-type';
import { TalentUserAvatar } from '../../../../common/ui/talent-user-avatar';
import { type State } from '../../../../common/utils/quick-find/state';
import {
	get3PProductFromProviderIdOrIntegrationId,
	getJiraSearchResultTypeMetadata,
} from '../../../../common/utils/search-result-utils';
import { getSummarizedAdfContent } from '../../../../common/utils/summarized-adf-content';
import {
	getResultConnectorSource,
	isResultSmartLink,
} from '../../../../common/utils/third-party-utils';
import { isDummyBoardUrl } from '../../../../common/utils/url';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { type QuickFindResultEvent } from '../../../../controllers/quick-find/utils';
import {
	useSearchSessionId,
	useThirdPartyConfigs,
	useUserShardUI,
} from '../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../controllers/store/bootstrap/accessors';
import { useQuickFindActions, useQuickFindQuery } from '../../../../controllers/store/quick-find';
import { useIsJiraHydrationLoading } from '../../../../controllers/store/search';

import { messages } from './messages';

const HoverCard = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_smart-card/hover-card" */ '@atlaskit/smart-card/hover-card'
	).then((mod) => ({ default: mod.HoverCard })),
);

const typeLabelContainer = xcss({
	'::first-letter': {
		textTransform: 'capitalize',
	},
});

const mapLatestUserActionToActivityEventType = (
	latestUserAction: string | undefined,
): ActivityEventType => {
	switch (latestUserAction?.toLowerCase()) {
		case 'viewed':
			return VIEWED_EVENT.VIEWED;
		case 'commented':
			return WORKED_EVENT.COMMENTED;
		case 'edited':
			return WORKED_EVENT.EDITED;
		case 'shared':
			return WORKED_EVENT.SHARED;
		default:
			return WORKED_EVENT.UPDATED;
	}
};

const mapConfluenceSearchResult = (
	result: SearchConfluencePartial,
	query: string,
	searchSessionId: string,
	serveUserShardUI?: boolean,
) => {
	const id = result.id;
	const title = result.title;
	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const space =
		isSearchConfluencePageBlogAttachment(result) && result.space?.name ? result.space.name : null;
	const spaceId = isSearchConfluencePageBlogAttachment(result) && result.space?.id;

	const isLiveDoc =
		isSearchConfluencePageBlogAttachment(result) &&
		result.confluenceEntity?.subtype === SearchConfluenceSubtype.LIVE &&
		fg('confluence_live_pages_open_beta_trait_opted_in');

	const icon = fg('rovo_search_confluence_icons') ? (
		getConfluenceEntityIcon({
			iconUrl: result.iconUrl,
			type: result.type,
			isLiveDoc,
		})
	) : (
		<ProductIcon size="small" product={ProductKeys.Confluence} />
	);

	const resultType = isLiveDoc ? <FormattedMessage {...messages.liveDoc} /> : result.type;
	const metadata = [
		'Confluence',
		[
			'type',
			<Box xcss={typeLabelContainer}>
				<SearchResultTypeText
					type={result.type as SearchResultType}
					defaultMessage={resultType}
					subtype={
						isSearchConfluencePageBlogAttachment(result)
							? result.confluenceEntity?.subtype
							: undefined
					}
				/>
			</Box>,
		],
		space,
	] satisfies SearchMetadataPart[];

	// Use user action data if available, otherwise fall back to lastModified
	const timestamp = result.latestUserActionTs || result.lastModified || '';
	const eventType = result.latestUserAction
		? mapLatestUserActionToActivityEventType(result.latestUserAction)
		: WORKED_EVENT.UPDATED;

	// Only set isMyActivityData to true when serveUserShardUI is enabled and both user action fields are present
	const isMyActivityData = Boolean(
		serveUserShardUI === true &&
		result.latestUserActionTs != null &&
		result.latestUserAction != null,
	);

	const rightMetadata = timestamp ? (
		<EventTypeMessage
			timestamp={timestamp}
			eventType={eventType}
			isMyActivityData={isMyActivityData}
		/>
	) : null;
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const url = getConfluenceUrlWithAnalyticsQueryParams({ url: result.url, query, searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		containerId: spaceId,
		contentType: `confluence ${result.type}`,
		product: `confluence`,
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapAtlasSearchResult = (
	result: SearchAtlasPartial,
	query: string,
	searchSessionId: string,
) => {
	const id = result.id;

	let icon: React.JSX.Element;
	if (fg('rovo_search_confluence_icons')) {
		icon = getAtlasEntityIcon({
			iconUrl: result.iconUrl,
			type: result.type,
		});
	} else {
		icon = <ProductIcon size="small" product={ProductKeys.Atlas} />;
	}

	const title = result.title;
	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const metadata = [
		'Atlas',
		[
			'type',
			<Box xcss={typeLabelContainer}>
				<SearchResultTypeText type={result.type as SearchResultType} defaultMessage={result.type} />
			</Box>,
		],
	] satisfies SearchMetadataPart[];

	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<ReturnIcon />
		</Inline>
	);

	const url = getGenericUrlWithAnalyticsQueryParams({ url: result.url, searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		contentType: `atlas ${result.type}`,
		product: `atlas`,
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapMercurySearchResult = (
	result: SearchMercuryPartial,
	query: string,
	searchSessionId: string,
) => {
	const { id } = result;
	let icon = <ProductIcon size="small" product={ProductKeys.Focus} />;
	let resultType, title, highlightedTitle, metadata;

	if (isSearchResultMercuryFocusAreaPartial(result)) {
		const { focusArea } = result;

		icon = (
			<FocusAreaIcon
				status={focusArea?.status?.key as FocusAreaStatusKey}
				health={focusArea?.health?.key as FocusAreaHealthKey}
			/>
		);
		title = result.title;
		highlightedTitle =
			title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;
		resultType = 'Focus area';
		metadata = [
			'Focus',
			[
				'type',
				<Box xcss={typeLabelContainer}>
					<SearchResultTypeText
						type={result.type as SearchResultType}
						defaultMessage={resultType}
					/>
				</Box>,
			],
			result.focusArea ? ['focusAreaType', result.focusArea?.focusAreaType.name] : null,
		] satisfies SearchMetadataPart[];
	}

	if (isSearchResultMercuryFocusAreaStatusUpdatePartial(result)) {
		title = getSummarizedAdfContent(result.description, query);
		highlightedTitle =
			title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;
		resultType = 'Focus area update';
		metadata = [
			'Focus',
			[
				'type',
				<Box xcss={typeLabelContainer}>
					<SearchResultTypeText
						type={result.type as SearchResultType}
						defaultMessage={resultType}
					/>
				</Box>,
			],
		] satisfies SearchMetadataPart[];
	}

	if (isSearchResultSpfAskPartial(result)) {
		title = result.title;
		highlightedTitle =
			title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;
		resultType = 'Ask';
		metadata = [
			'Focus',
			[
				'type',
				<Box xcss={typeLabelContainer}>
					<SearchResultTypeText
						type={result.type as SearchResultType}
						defaultMessage={resultType}
					/>
				</Box>,
			],
		] satisfies SearchMetadataPart[];
	}

	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<ReturnIcon />
		</Inline>
	);

	const url = getGenericUrlWithAnalyticsQueryParams({ url: result.url, searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		contentType: `mercury ${result.type}`,
		product: `mercury`,
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapTalentSearchResult = (
	result: SearchTalentPartial,
	query: string,
	searchSessionId: string,
) => {
	const { id } = result;
	let icon = <ProductIcon size="small" />;
	let resultType, title, highlightedTitle, metadata;
	const querySplit = query.split(' ');

	if (isSearchTalentPartial(result)) {
		const { position } = result;

		let jobTitle: string = '';
		let externalPositionKey: string = '';
		let positionKey: string = '';
		const preferredName: string = position?.worker?.preferredName || '';

		icon = <TalentUserAvatar isQuickFind src={position?.worker?.user?.picture} />;

		for (const field of position?.fieldValues ?? []) {
			switch (field.fieldId) {
				case 'positionKey':
					if ('stringValue' in field.fieldValue) {
						positionKey = field.fieldValue.stringValue || '';
					}
					break;
				case 'positionPositionTitle':
					if ('stringValue' in field.fieldValue) {
						jobTitle = field.fieldValue.stringValue || '';
					}
					break;
				case 'positionExternalPositionKey':
					if ('stringValue' in field.fieldValue) {
						externalPositionKey = field.fieldValue.stringValue || '';
					}
					break;
				default:
					break;
			}
		}

		title = preferredName ? `${positionKey}: ${preferredName}` : positionKey;
		highlightedTitle =
			title && query ? highlightMatchedText(title, querySplit).highlightedTitle : undefined;
		resultType = 'Position';
		metadata = [
			'Talent',
			[
				'type',
				<Box xcss={typeLabelContainer}>
					<SearchResultTypeText
						type={result.type as SearchResultType}
						defaultMessage={resultType}
					/>
				</Box>,
			],
			['jobTitle', jobTitle],
			[
				'externalPositionKey',
				externalPositionKey ? (
					<FormattedMessage
						{...messages.talentExternalPositionKey}
						values={{ positionKey: externalPositionKey }}
					/>
				) : null,
			],
		] satisfies SearchMetadataPart[];
	}

	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<ReturnIcon />
		</Inline>
	);

	const url = getGenericUrlWithAnalyticsQueryParams({ url: result.url, searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		contentType: `talent ${result.type}`,
		product: `talent`,
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapThirdPartySearchResult = (
	result: SearchResultGraphDocument<SearchResultEntity>,
	query: string,
	searchSessionId: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	const { id, providerId, integrationId, subtype, lastModifiedDate, title } = result;

	const product = get3PProductFromProviderIdOrIntegrationId({
		thirdPartyConfigs,
		providerId,
		integrationId,
	});

	if (!isProductKey(product)) {
		return;
	}

	const { resourceType } = AnyAri.parse(id);

	const icon = <ProductIcon size="small" product={product} />;

	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const typeLabel = getSubtypeLabel({
		product,
		subtype,
		resourceType,
	});

	const parentName = isDocumentEntity(result) ? result.entity?.parent?.displayName : null;

	/**
	 * Use constant config as fallback
	 * Loom is not included in thirdPartyConfigs from API
	 */
	const productDisplayName =
		thirdPartyConfigs[product as ProductKeys3P]?.displayName ||
		ConstantThirdPartyConfigs.get(product)?.displayName;

	const metadata = [
		['product', productDisplayName],
		[
			'type',
			typeLabel ? (
				<Box xcss={typeLabelContainer}>
					<FormattedMessage {...typeLabel} />
				</Box>
			) : subtype ? (
				<Box xcss={typeLabelContainer}>
					{subtype.charAt(0).toUpperCase() + subtype.substring(1).toLowerCase()}
				</Box>
			) : null,
		],
		parentName ?? null,
	] satisfies SearchMetadataPart[];

	const timestamp = lastModifiedDate || '';
	const eventType = WORKED_EVENT.UPDATED;
	const rightMetadata = lastModifiedDate ? (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={false} />
	) : null;
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const url = getGenericUrlWithAnalyticsQueryParams({ url: result.url, searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		contentType: `${product} ${subtype}`,
		product: product,
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapJiraSearchResult = (
	result: SearchJiraPartial,
	query: string,
	searchSessionId: string,
	isStatusLoading?: boolean,
	primaryProduct?: ProductKeys,
) => {
	const id = result.id;
	const analyticsMetadata = {
		contentId: id,
		contentType: `jira ${result.type}`,
		product: `jira`,
	};
	const isIssue = isSearchResultJiraIssue(result);
	const title = isIssue
		? [result.issue?.key, result.title].filter(Boolean).join(': ')
		: result.title;

	let icon: React.JSX.Element;
	if (fg('rovo_search_jira_icons')) {
		const issueIconUrl = isIssue ? result.issue?.issueTypeAvatarUrl : undefined;
		const boardIconUrl = isSearchResultJiraBoard(result) ? result.board?.iconUrl : undefined;

		const iconUrl = boardIconUrl || issueIconUrl || result.iconUrl;

		icon = getJiraEntityIcon({ iconUrl, type: result.type, isIssue });
	} else {
		try {
			icon =
				result.iconUrl && new URL(result.iconUrl) ? (
					// eslint-disable-next-line @atlassian/a11y/alt-text -- See https://go/a11y-alt-text for more details
					<Image src={result.iconUrl} />
				) : (
					<ProductIcon size="small" product={ProductKeys.Jira} />
				);
		} catch {
			icon = <ProductIcon size="small" product={ProductKeys.Jira} />;
		}
	}

	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const status = isSearchResultJiraIssue(result)
		? getStatus(result.status?.name, result.status?.colorName, isStatusLoading)
		: null;

	const metadata: SearchMetadataPart[] = [];
	metadata.push('Jira');

	if (isSearchResultJiraIssue(result)) {
		const issueType = result.issue?.issueTypeField?.issueType;
		issueType && metadata.push(issueType.name);

		const project = result.issue?.fieldsById.edges[0]?.node.project.name;
		project && metadata.push(project);
	} else if (isSearchResultJiraBoard(result)) {
		metadata.push([
			'type',
			<Box xcss={typeLabelContainer}>
				<SearchResultTypeText type={result.type as SearchResultType} defaultMessage={result.type} />
			</Box>,
		]);

		const containerProjectName =
			result.container && 'projectName' in result.container && result.container.projectName;
		const containerUserName =
			result.container && 'userName' in result.container && result.container.userName;
		const containerName = containerProjectName || containerUserName || undefined;
		if (containerName) {
			metadata.push(containerName);
		}
	} else {
		const resultType = getJiraSearchResultTypeMetadata(result.type);
		metadata.push([
			'type',
			<Box xcss={typeLabelContainer}>
				<SearchResultTypeText type={resultType as SearchResultType} defaultMessage={result.type} />
			</Box>,
		]);
	}

	const rightMetadata = isSearchResultJiraIssue(result) ? (
		<EventTypeMessage
			timestamp={result.lastModifiedDate || ''}
			eventType={WORKED_EVENT.UPDATED}
			isMyActivityData={false}
		/>
	) : null;

	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			<ReturnIcon />
		</Inline>
	);

	let jiraResultUrl;
	if (isSearchResultJiraIssue(result)) {
		jiraResultUrl = result.issue?.webUrl || result.url;
	} else if (isSearchResultJiraBoard(result)) {
		const notDummyUrl = isDummyBoardUrl(result.url) ? undefined : result.url;
		jiraResultUrl = jiraResultUrl = result.board?.boardFrontendUrl || notDummyUrl;
		if (!jiraResultUrl) {
			// We don't want the board with no url to be displayed. Returning null prevents rendering
			return null;
		}
	} else {
		jiraResultUrl = result.url;
	}

	const customParams =
		primaryProduct?.includes(ProductKeys.Jira) && fg('search_quick_find_query_params_enable')
			? new Map<string, string>([['referrer', SearchExperience.QUICK_FIND]])
			: new Map<string, string>();
	const url = getGenericUrlWithAnalyticsQueryParams({
		url: jiraResultUrl,
		searchSessionId,
		customParams,
	});
	return {
		id,
		title,
		highlightedTitle,
		icon,
		status,
		analyticsMetadata,
		metadata,
		highlightedRightMetadata,
		rightMetadata,
		url,
	};
};

const mapAssetsSearchResult = (
	result: SearchAssetsPartial,
	query: string,
	searchSessionId: string,
) => {
	const id = result.id;

	const title = result.title;
	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const metadata = isSearchResultAssetsObjectSchema(result)
		? ([
				'Assets',
				[
					'type',
					<Box xcss={typeLabelContainer}>
						<SearchResultTypeText
							type={result.type as SearchResultType}
							defaultMessage={result.type}
						/>
					</Box>,
				],
			] satisfies SearchMetadataPart[])
		: ([
				'Assets',
				[
					'type',
					<Box xcss={typeLabelContainer}>
						{isSearchResultAssetsObjectType(result) ? (
							`Object ${result.type}`
						) : (
							<SearchResultTypeText
								type={result.type as SearchResultType}
								defaultMessage={result.type}
							/>
						)}
					</Box>,
				],
				['schema', <Box xcss={typeLabelContainer}>{result.objectSchemaEntity.name}</Box>],
			] satisfies SearchMetadataPart[]);

	const timestamp = result.lastModifiedDate || '';
	const eventType = WORKED_EVENT.UPDATED;
	const rightMetadata = result.lastModifiedDate ? (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={false} />
	) : null;
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const getAssetsUrl = (result: SearchAssetsPartial) => {
		if (isSearchResultAssetsObjectSchema(result)) {
			return result.objectSchemaEntity.links.web;
		} else if (isSearchResultAssetsObjectType(result)) {
			return result.objectTypeEntity.links.web;
		} else {
			return result.objectEntity.links.web;
		}
	};
	let iconUrl: string | null = null;

	if (isSearchResultAssetsObjectSchema(result)) {
		iconUrl = null;
	} else if (isSearchResultAssetsObjectType(result)) {
		iconUrl = result.objectTypeEntity.icon.url48;
	} else if (isSearchResultAssetsObject(result)) {
		iconUrl = result.objectEntity.avatar.url48;
	}
	const icon = getAssetsEntityIcon({
		iconUrl,
		type: result.type,
		size: 'small',
	});

	const url = getGenericUrlWithAnalyticsQueryParams({ url: getAssetsUrl(result), searchSessionId });

	const analyticsMetadata = {
		contentId: id,
		contentType: `assets ${result.type}`,
		product: `assets`,
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapSearchResult = (
	result: SearchPageNode,
	query: string,
	searchSessionId: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	isStatusLoading?: boolean,
	serveUserShardUI?: boolean,
	primaryProduct?: ProductKeys,
) => {
	if (isSearchConfluencePartial(result)) {
		return mapConfluenceSearchResult(result, query, searchSessionId, serveUserShardUI);
	} else if (isSearchAtlasPartial(result)) {
		return mapAtlasSearchResult(result, query, searchSessionId);
	} else if (isSearchMercuryPartial(result)) {
		return mapMercurySearchResult(result, query, searchSessionId);
	} else if (isSearchTalentPartial(result) && fg('talent_global_search')) {
		return mapTalentSearchResult(result, query, searchSessionId);
	} else if (isSearchResultGraphEntity(result)) {
		return mapThirdPartySearchResult(result, query, searchSessionId, thirdPartyConfigs);
	} else if (isSearchJiraPartial(result)) {
		return mapJiraSearchResult(result, query, searchSessionId, isStatusLoading, primaryProduct);
	} else if (isSearchAssetsPartial(result) && fg('assets_rovo_search_all')) {
		return mapAssetsSearchResult(result, query, searchSessionId);
	} else {
		return null;
	}
};

type MapSearchResultReturnType = ReturnType<typeof mapSearchResult>;

type Props = {
	result: SearchPageNode;
	state: State;
	onClick?: (props: QuickFindResultEvent) => void;
	onContextMenu?: (props: QuickFindResultEvent) => void;
	onHighlighted?: (props: QuickFindResultEvent) => void;
};

export const ProductEntity = ({
	result,
	state,
	onClick,
	onContextMenu,
	onHighlighted,
}: Props): React.JSX.Element | null => {
	const quickFindQuery = useQuickFindQuery();
	const { setHoverCardOpen } = useQuickFindActions();
	const [primaryProduct] = usePrimaryProduct();
	const [searchSessionId] = useSearchSessionId();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const isHydrationLoading = useIsJiraHydrationLoading();
	const serveUserShardUI = useUserShardUI();
	const { invalidateItem } = useItemsAnalyticsContext();

	const mappedResult = mapSearchResult(
		result,
		quickFindQuery,
		searchSessionId,
		thirdPartyConfigs,
		isHydrationLoading,
		serveUserShardUI,
		primaryProduct,
	);

	const additionalAnalyticsAttributes = useMemo(
		() => ({
			metadata: {
				...mappedResult?.analyticsMetadata,
				connectorType: getResultConnectorSource({
					connectorType: result.connectorType,
					resultNodeId: result.id,
					thirdPartyConfigs,
					productKey: mappedResult?.analyticsMetadata.product,
				}),
			},
			is3pLink: is3pProductKeyForAnalytics(mappedResult?.analyticsMetadata.product),
			is3pSmartLink: isResultSmartLink({
				resultAri: result.id,
				thirdPartyConfigs,
				productKey: mappedResult?.analyticsMetadata.product,
				connectorType: result.connectorType,
			}),
		}),
		[mappedResult, result.id, thirdPartyConfigs, result.connectorType],
	);

	const onHoverCardVisibilityChange = useCallback(
		(isOpen: boolean) => {
			setHoverCardOpen(result.id, isOpen);
		},
		[setHoverCardOpen, result.id],
	);

	const onResultClicked = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			onClick?.({
				event,
				data,
				state,
				additionalAnalyticsAttributes,
			});

			if (fg('search_qf_fix_close_hover_card')) {
				onHoverCardVisibilityChange(false);
			}
		},
		[additionalAnalyticsAttributes, onClick, state, onHoverCardVisibilityChange],
	);

	const onResultContextMenu = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			onContextMenu?.({
				event,
				data,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onContextMenu, state],
	);

	const onResultHighlighted = useCallback(
		(data: SearchResultEventArguments) => {
			onHighlighted?.({
				data,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onHighlighted, state],
	);

	if (!mappedResult) {
		invalidateItem(result.id);
		return null;
	}

	const content = (
		<SearchResult
			{...mappedResult}
			resultType="searchResults"
			product={mappedResult.analyticsMetadata.product}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onResultHighlighted={onResultHighlighted}
			connectorType={additionalAnalyticsAttributes.metadata.connectorType}
		/>
	);

	return isSearchResultJiraIssue(result) ? (
		<JiraResult
			result={result}
			content={content}
			mappedResult={mappedResult}
			onHoverCardVisibilityChange={onHoverCardVisibilityChange}
		/>
	) : (
		content
	);
};

interface JiraResultProps {
	onHoverCardVisibilityChange: (isOpen: boolean) => void;
	mappedResult: MapSearchResultReturnType;
	content: React.JSX.Element;
	result: SearchPageNode;
}

const JiraResult = ({
	result,
	content,
	mappedResult,
	onHoverCardVisibilityChange,
}: JiraResultProps) => {
	const { invalidateItem } = useItemsAnalyticsContext();
	const [primaryProduct] = usePrimaryProduct();
	const enableJiraIssueHoverCard =
		primaryProduct === ProductKeys.Jira && fg('enable_hover_card_jira_issues_quick_find');

	if (!mappedResult) {
		invalidateItem(result.id);
		return null;
	}

	return enableJiraIssueHoverCard ? (
		<ErrorBoundary fallback={content} section="result-hover-card">
			<Suspense fallback={content}>
				<HoverCard
					url={mappedResult.url}
					actionOptions={{
						hide: false,
						exclude: [CardAction.PreviewAction],
					}}
					role="dialog"
					titleId={mappedResult.title}
					label="quick-find-smart-hover-card"
					onVisibilityChange={onHoverCardVisibilityChange}
					shouldRenderToParent
				>
					{content}
				</HoverCard>
			</Suspense>
		</ErrorBoundary>
	) : (
		content
	);
};
